import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { BUTTON_QUIET, HelpConfirmation, HelpRequestForm } from "./HelpDeskViews";
import { useAuth } from "../../lib/auth";
import {
  buildTicketInsert,
  createTicket,
  describeBrowser,
  friendlyError,
  uploadScreenshot,
  validateRequest,
} from "../../lib/helpDesk/helpDesk.mjs";
import type { HelpTicket, HelpUrgency } from "../../lib/helpDesk/helpDesk.mjs";
import { getSupabase } from "../../lib/supabase";
import { useTenantId } from "../../lib/tenant";

/**
 * The Help button in the dashboard's top bar (issue #1096) and the request
 * dialog it opens. The page, the customer, the user and the browser are
 * attached automatically: the page here, the customer and user by the 0110
 * trigger from the session. The request is a row in public.help_ticket; the
 * droplet opens the GitHub issue and emails Meridian from it.
 */
export default function HelpButton(props: {
  slug: string;
  pageLabel: string | undefined;
  staffPreview: boolean;
}) {
  const { slug, pageLabel, staffPreview } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [urgency, setUrgency] = useState<HelpUrgency>("normal");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ body?: string; screenshot?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<HelpTicket | null>(null);
  const opener = useRef<HTMLButtonElement>(null);

  const pagePath = `${location.pathname}${location.search}`;
  const browser = describeBrowser(
    typeof navigator !== "undefined" ? navigator.userAgent : "",
    typeof window !== "undefined" ? { width: window.innerWidth, height: window.innerHeight } : null,
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function reset() {
    setBody("");
    setUrgency("normal");
    setScreenshot(null);
    setErrors({});
    setError(null);
    setSent(null);
  }

  function close() {
    setOpen(false);
    reset();
    opener.current?.focus();
  }

  async function submit() {
    const check = validateRequest({ body, urgency, screenshot });
    setErrors(check.errors);
    if (!check.ok) return;
    const userId = session?.user.id;
    if (!tenantId || !userId) {
      setError("Still loading your account. Try again in a moment.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const supabase = getSupabase();
    let screenshotPath: string | null = null;
    if (screenshot) {
      const up = await uploadScreenshot(supabase, {
        tenantId,
        userId,
        file: screenshot,
        id: crypto.randomUUID(),
      });
      if (up.error) {
        setSubmitting(false);
        setErrors({ screenshot: "The screenshot did not upload. Try again or send without it." });
        return;
      }
      screenshotPath = up.path;
    }
    const { ticket, error: insertError } = await createTicket(
      supabase,
      buildTicketInsert({
        tenantId,
        body,
        urgency,
        pagePath,
        pageTitle: pageLabel ?? null,
        browser,
        screenshotPath,
      }),
    );
    setSubmitting(false);
    if (insertError || !ticket) {
      setError(friendlyError(insertError));
      return;
    }
    setSent(ticket);
  }

  return (
    <>
      <button
        ref={opener}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        style={{ ...BUTTON_QUIET, padding: "5px 12px" }}
      >
        Help
      </button>
      {open ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10, 15, 13, 0.45)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 100,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-dialog-title"
            style={{
              background: "var(--bg-card)",
              borderRadius: 14,
              padding: "20px 24px",
              width: "min(560px, 100%)",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "var(--shadow-card)",
              display: "grid",
              gap: 14,
            }}
          >
            <h2 id="help-dialog-title" style={{ margin: 0, fontSize: 18 }}>
              {sent ? "Thanks" : "Ask Meridian for help"}
            </h2>
            {sent ? (
              <HelpConfirmation
                ticket={sent}
                onViewRequests={() => {
                  const id = sent.id;
                  close();
                  navigate(`/${slug}/Help?request=${id}`);
                }}
                onClose={close}
              />
            ) : (
              <HelpRequestForm
                pageLabel={pageLabel ? `${pageLabel} (${pagePath})` : pagePath}
                browserLabel={browser}
                body={body}
                urgency={urgency}
                screenshotName={screenshot?.name ?? null}
                errors={errors}
                submitting={submitting}
                error={error}
                staffPreview={staffPreview}
                onBodyChange={setBody}
                onUrgencyChange={setUrgency}
                onScreenshotChange={setScreenshot}
                onSubmit={() => void submit()}
                onCancel={close}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
